import api from '../../../api';

/**
 * Helper: format date as dd/mm/yyyy
 */
const formatDateToDDMMYYYY = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Send lead assignment email to assigned user
 * @param {Object} emailData - Email data with to, subject, message, leadId
 * @returns {Promise} - API response
 */
export const sendLeadAssignmentEmail = async (emailData) => {
  try {
    console.log('=== DEBUG: API Send Lead Assignment Email ===');
    console.log('Email data to send:', emailData);

    const response = await api.post('/crm/leads/send-email', emailData);
    console.log('Email API response:', response);

    return response.data;
  } catch (error) {
    console.error('Error sending lead assignment email:', error);
    throw error;
  }
};

/**
 * Get lead email templates
 * @returns {Promise<Array>} - Array of email templates
 */
export const getLeadEmailTemplates = async () => {
  try {
    const response = await api.get('/email/templates/template-id/lead');

    // Handle different response formats
    let templates = [];
    if (response.data) {
      if (Array.isArray(response.data)) {
        templates = response.data;
      } else if (response.data.content && Array.isArray(response.data.content)) {
        templates = response.data.content;
      } else if (typeof response.data === 'object') {
        // Single template object - wrap in array
        templates = [response.data];
      }
    }

    return templates;
  } catch (error) {
    console.error('Error fetching lead email templates:', error);
    // Return empty array if no templates found
    return [];
  }
};

/**
 * Check email settings
 * @returns {Promise<Object|null>} - Email settings or null
 */
export const checkEmailSettings = async () => {
  try {
    const response = await api.get('/email/settings');
    return response.data;
  } catch (error) {
    console.error('Error checking email settings:', error);
    return null;
  }
};

/**
 * Send lead assignment email to user with template
 * @param {string} userEmail - Email of the assigned user
 * @param {Object} emailSettings - Email configuration settings
 * @param {Array} emailTemplates - Available email templates
 * @param {Object} leadData - Lead data for email placeholders
 * @returns {Promise} - Email send result
 */
export const sendLeadEmailToUser = async (userEmail, emailSettings, emailTemplates, leadData) => {
  try {
    console.log('=== DEBUG: Sending Lead Email ===');
    console.log('User Email:', userEmail);
    console.log('Email Settings:', emailSettings);
    console.log('Email Templates:', emailTemplates);
    console.log('Lead Data:', leadData);

    // Check if email settings are configured
    if (!emailSettings) {
      console.warn('⚠️ Email settings not configured');
      throw new Error('Email settings not configured');
    }

    // Check if user email is provided
    if (!userEmail) {
      console.warn('⚠️ User email not provided');
      throw new Error('User email not provided');
    }

    // Get email template for leads
    let emailTemplate = null;
    if (Array.isArray(emailTemplates) && emailTemplates.length > 0) {
      emailTemplate = emailTemplates.find(template =>
        template.templateId === 'lead' ||
        template.id === 'lead'
      );
    }

    // Prepare email content with defaults
    let subject = `New Lead Assigned: ${leadData.projectName || 'Lead'}`;
    let message = `Dear ${leadData.assignedUser || 'User'},

You have been assigned a new lead:

Project Name: ${leadData.projectName || 'N/A'}
Company Name: ${leadData.companyName || 'N/A'}
Contact Name: ${leadData.contactName || 'N/A'}
Email: ${leadData.email || 'N/A'}
Stage: ${leadData.stage || 'N/A'}
Sector: ${leadData.sector || 'N/A'}
Description: ${leadData.projectDescription || 'N/A'}

Please log in to the system to view and manage this lead.

Best regards,
CRM System`;

    // Use template if available and replace placeholders
    if (emailTemplate) {
      subject = emailTemplate.subject || subject;
      let templateContent = emailTemplate.content || message;

      // Replace placeholders with actual lead data
      templateContent = templateContent
        .replace(/\{\{assigned_user\}\}/g, leadData.assignedUser || 'User')
        .replace(/\{\{project_name\}\}/g, leadData.projectName || 'N/A')
        .replace(/\{\{company_name\}\}/g, leadData.companyName || 'N/A')
        .replace(/\{\{contact_name\}\}/g, leadData.contactName || 'N/A')
        .replace(/\{\{email\}\}/g, leadData.email || 'N/A')
        .replace(/\{\{stage\}\}/g, leadData.stage || 'N/A')
        .replace(/\{\{sector\}\}/g, leadData.sector || 'N/A')
        .replace(/\{\{project_description\}\}/g, leadData.projectDescription || 'N/A')
        .replace(/\{\{created_by\}\}/g, leadData.createdBy || 'System')
        .replace(/\{\{created_at\}\}/g, formatDateToDDMMYYYY(leadData.createdAt) || formatDateToDDMMYYYY(new Date()));

      message = templateContent;

      // Also replace placeholders in subject
      subject = subject
        .replace(/\{\{assigned_user\}\}/g, leadData.assignedUser || 'User')
        .replace(/\{\{project_name\}\}/g, leadData.projectName || 'N/A')
        .replace(/\{\{company_name\}\}/g, leadData.companyName || 'N/A');
    }

    // Prepare email data
    const emailData = {
      to: userEmail,
      subject: subject,
      message: message,
      leadId: leadData.id,
      includeAttachment: false
    };

    console.log('=== DEBUG: Email Data Prepared ===');
    console.log('Email Data:', emailData);

    // Send email
    const result = await sendLeadAssignmentEmail(emailData);

    console.log('✅ Lead email sent successfully');
    return result;
  } catch (error) {
    console.error('✗ Error sending lead email:', error);
    throw error;
  }
};







